import { Input, Pagination, Select, Switch } from 'antd';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/api/client';
import { itemsKey, useItemCategories, useItems, type Item } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { IconAction } from '@/components/IconAction';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { absent } from '@core/format/dates';
import type { Session } from '@core/session/types';

import { DesignPage } from './items/DesignPage';
import { MediaDrawer } from './MediaDrawer';

type Editing = { mode: 'create' } | { mode: 'edit'; item: Item };

const pageSize = 25;

/**
 * The item catalogue — what the shop advertises, one row per design (spec Section 6).
 *
 * The first screen here that pages and searches server-side, because it is the first table that
 * can hold thousands of rows. That changes the shape of the page in two visible ways: the toolbar
 * carries the filters rather than the table doing its own client-side sorting, and the pager sits
 * outside the card so it stays put while the rows underneath it change.
 *
 * Like the categories screen, its real job is making inheritance legible. An item that sets no
 * turnaround buffer and no HSN code is not "0 days, no code" — it is whatever its category says,
 * and rendering a bare value would quietly teach the shop the wrong thing about its own
 * availability and its own tax position.
 */
export function ItemsPage({ session }: { session: Session }) {
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState<number | undefined>();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [picturing, setPicturing] = useState<Item | null>(null);

  const debouncedSearch = useDebounced(search);

  // Any narrowing invalidates the page number: filtering to a category while on page 4 of the
  // whole catalogue lands on an empty page, which reads as "no matches" rather than "wrong page".
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryId, includeInactive]);

  const items = useItems(session.branch?.id, {
    search: debouncedSearch,
    categoryId,
    includeInactive,
    page,
    pageSize,
  });

  // Every category, inactive included: an item may sit in a category that has been retired, and a
  // dropdown that cannot show where the item currently is makes the edit form lie.
  const categories = useItemCategories(session.branch?.id, true);

  const remove = useApiMutation<void, Item>({
    mutationFn: (item) => apiFetch<void>(`/api/item/${item.id}`, { method: 'DELETE' }),
    success: (_result, item) => `${item.name} deleted.`,
    invalidates: [itemsKey],
  });

  const columns: DataViewColumn<Item>[] = [
    {
      title: 'Item',
      dataIndex: 'name',
      // The heading on a phone. It already carries the category path and brand underneath the
      // name, which is exactly what a card wants and why it needs no separate treatment.
      primary: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{row.name}</span>
            {/*
              Only the branch-local rows are marked, as on the categories screen: shared is the
              ordinary case, and what an operator needs to spot is the exception.
            */}
            {!row.isShared && <StatusBadge tone="brand">This branch</StatusBadge>}
            {!row.isActive && <StatusBadge tone="warning">Inactive</StatusBadge>}
          </div>
          <span className="text-[12px] text-default">
            {row.categoryPath}
            {row.brand ? ` · ${row.brand}` : ''}
          </span>
        </div>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      width: 160,
      render: (_, row) => <span className="font-mono-nums font-semibold">{row.code}</span>,
    },
    // No "Rental" column: it showed the old base rate, which no booking is charged. A design's price
    // is its rate card, on /rate-cards and on each garment's row in the stock list.
    {
      title: 'Replacement',
      dataIndex: 'replacementValue',
      width: 130,
      align: 'right',
      responsive: ['xl'],
      render: (_, row) => <Amount value={row.replacementValue} />,
    },
    {
      title: 'Turnaround',
      dataIndex: 'effectiveTurnaroundBufferDays',
      width: 140,
      align: 'right',
      render: (_, row) => (
        <Inherited own={row.turnaroundBufferDays} effective={row.effectiveTurnaroundBufferDays}>
          {(days) => `${days} day${days === 1 ? '' : 's'}`}
        </Inherited>
      ),
    },
    {
      title: 'HSN / SAC',
      key: 'gst',
      width: 140,
      responsive: ['xl'],
      // Off the card. The codes are inherited from the category for almost every item and nobody
      // picks a garment off a phone by its SAC code — the edit drawer is where they matter.
      hideOnCard: true,
      render: (_, row) => (
        <Inherited
          own={row.hsnCode ?? row.sacCode}
          effective={`${row.effectiveHsnCode ?? absent} / ${row.effectiveSacCode ?? absent}`}
        >
          {(codes) => codes}
        </Inherited>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          {/* Its own action rather than a tab inside the edit drawer: uploading is a separate
              errand from correcting a rate, and it must not require saving a form to keep. */}
          <IconAction
            icon="icon icon-image"
            label={`Photographs for ${row.name}`}
            onClick={() => setPicturing(row)}
          />

          <WriteAction session={session} required="Item:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.name}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', item: row })}
              />
            )}
          </WriteAction>

          <RecordAction session={session} required="Item:Delete">
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.name}`}
                title="Delete this item?"
                // Deactivating is the operation that is always safe, and saying so here is what
                // stops a shop deleting a design it only meant to stop offering.
                description="If you only want to stop offering it, edit the item and switch it inactive instead."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  // A full page rather than a drawer — the rule settled on 2026-09-08 — replacing the list rather
  // than sitting over it, as the category page does.
  if (editing) {
    return (
      <DesignPage
        session={session}
        item={editing.mode === 'edit' ? editing.item : undefined}
        categories={categories.data ?? []}
        onClose={() => setEditing(null)}
      />
    );
  }

  if (items.isError) {
    return <ApiErrorAlert error={items.error} />;
  }

  const total = items.data?.total ?? 0;

  return (
    <>
      <PageHeading
        title="Items"
        subtitle="The designs the shop rents. Physical garments hang off these as units."
        actions={
          <WriteAction session={session} required="Item:Create">
            {(disabled) => (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                disabled={disabled}
                onClick={() => setEditing({ mode: 'create' })}
              >
                New item
              </Button>
            )}
          </WriteAction>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        {/* The two filters go full width on a phone rather than wrapping into ragged part-widths;
            the toggle keeps its own row instead of being pushed there by `ms-auto`. */}
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search code, name or brand"
            allowClear
            prefix={<i className="icon icon-search text-default" />}
            className="max-w-72 max-sm:max-w-none max-sm:flex-[1_0_100%]"
          />

          <Select
            value={categoryId}
            onChange={setCategoryId}
            options={(categories.data ?? []).map((category) => ({
              label: `${'  '.repeat(category.depth)}${category.name}`,
              value: category.id,
            }))}
            allowClear
            placeholder="Every category"
            className="min-w-56 max-sm:min-w-0 max-sm:flex-1"
          />

          <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-default sm:ms-auto">
            <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
            Show inactive
          </label>
        </div>

        {/* Paged by the server either way, so antd's own pager stays off and the one below drives
            the query — that is true of the cards as much as the table. */}
        <DataView<Item>
          columns={columns}
          dataSource={items.data?.items ?? []}
          loading={items.isLoading}
          empty={
            <EmptyState
              icon="icon icon-shirt"
              title={
                debouncedSearch || categoryId !== undefined
                  ? 'Nothing matches those filters'
                  : 'No items at this branch'
              }
              hint={
                debouncedSearch || categoryId !== undefined
                  ? 'Clear the search or pick a different category.'
                  : `Nothing shared company-wide, and nothing local to ${
                      session.branch?.code ?? 'this branch'
                    }. Items marked "shared" appear at every branch.`
              }
            />
          }
        />

        {total > pageSize && (
          <div className="flex justify-end border-t border-border-color py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} items`}
            />
          </div>
        )}
      </Card>

      {picturing && (
        <MediaDrawer
          session={session}
          scope={{ level: 'item', itemId: picturing.id, name: picturing.name }}
          onClose={() => setPicturing(null)}
        />
      )}
    </>
  );
}

/**
 * A value, marked when it came from the category rather than the item.
 *
 * "2 days" and "2 days (inherited)" mean different things to whoever is about to change one:
 * editing the second has no effect unless they set an override, and a screen rendering both the
 * same way makes that invisible.
 */
function Inherited<T extends string | number>({
  own,
  effective,
  children,
}: {
  own: string | number | null;
  effective: T;
  children: (value: T) => string;
}) {
  if (own !== null) {
    return <span className="font-mono-nums">{children(effective)}</span>;
  }

  return (
    <span className="font-mono-nums text-default">
      {children(effective)}
      <span className="ms-1.5 text-[11px]">inherited</span>
    </span>
  );
}

/**
 * An amount, or a dash.
 *
 * No currency symbol: the money quad records the company's base currency and the client is not
 * told which one that is yet. Multi-currency is Phase 6, and inventing a hardcoded rupee sign here
 * would be a thing to find and remove then.
 */
function Amount({ value }: { value: number | null }) {
  if (value === null) {
    return <span className="text-default">{absent}</span>;
  }

  return <span className="font-mono-nums">{value.toLocaleString('en-IN')}</span>;
}
